import { Client } from '@/api/main/client'
import { directions } from '@/api/navigationApi/sdk.gen'
import { calculateAzimuth, calculateCorrectedAzimuth } from '@/utils/azimuth'
import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { add, differenceInSeconds, isAfter, sub, milliseconds } from 'date-fns'
import { useCurrentTime } from '@/stores/currentTime'
import { useEffect, useRef } from 'react'
import { NavigationDataResponse } from '@/api/navigationApi/types.gen'
import { determineSourceNode, determineDestinationNode } from './utils/mapGLHelpers'
import { determineGarageCoordinates } from './utils/mapUtils'
import { DROPOFF } from './dropoffConstants'
import { decode as decodePolyline } from '@googlemaps/polyline-codec'

const MIN_ROUTE_DURATION_SECONDS = 5 * 60 // 5 minutes
const MIN_PATROLLING_TIME_MINUTES = 5
const AUTO_REFETCH_DISTANCE_METERS = 100 // Auto-refetch when within 100m of destination
const REFETCH_COOLDOWN_MS = 5000 // Prevent refetching too frequently (5 seconds)

// Garage destination, configurable via VITE_GARAGE_COORDINATES ("lng:lat"); falls back to a default point.
const EXPLICIT_DROPOFF_POINT = determineGarageCoordinates()

const STATIC_QUERY_OPTIONS = {
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
} as const

/**
 * Navigation mode determines what the driver should be doing
 */
export type NavigationMode =
  | {
      type: 'MANUAL_GARAGE'
      reason: 'go_to_garage_enabled'
    }
  | {
      type: 'AUTOMATIC_GARAGE'
      reason: 'shift_ending'
      timeRemainingSeconds: number
      dropoffStartDate: Date
      driveDurationSeconds: number
      scheduledEndTime: string
    }
  | {
      type: 'NEXT_ROUTE'
      reason: 'between_drives'
    }
  | {
      type: 'NONE'
      reason: 'no_navigation_needed'
    }

const extractVehicleData = (navigationData: NavigationDataResponse | undefined) => {
  if (!navigationData?.patrol_vehicle) {
    return { originCoord: undefined, originAzimuth: undefined }
  }

  const vehicle = navigationData.patrol_vehicle
  const hasPosition = vehicle.position.latitude !== undefined && vehicle.position.longitude !== undefined

  return {
    originCoord: hasPosition
      ? {
          latitude: vehicle.position.latitude as number,
          longitude: vehicle.position.longitude as number,
        }
      : undefined,
    originAzimuth: hasPosition ? vehicle.azimuth : undefined,
  }
}

const shouldEnableNextRouteDirections = (
  navigationData: NavigationDataResponse | undefined,
  state: string | undefined,
  originCoord: { latitude: number; longitude: number } | undefined
) => {
  return (
    navigationData !== undefined &&
    navigationData.future_street_segments?.[0] !== undefined &&
    (state === 'Fetched' || state === 'Paused') &&
    originCoord !== undefined
  )
}

const buildNextRouteQueryKey = (
  segmentId: string | undefined,
  dropoffPoint: { longitude: number; latitude: number } | undefined
) => ['nextRoute', segmentId, dropoffPoint]

const buildDropoffPointQueryKey = (navigationData: NavigationDataResponse | undefined) => [
  'dropoffPoint',
  navigationData?.scheduled_shift?.dropoff_point?.longitude,
  navigationData?.scheduled_shift?.dropoff_point?.latitude,
]

const buildExplicitDropoffPointQueryKey = (
  segmentId: string | undefined,
  dropoffPoint: { longitude: number; latitude: number } | undefined
) => ['explicitDropoffPoint', segmentId, dropoffPoint]

export const resolveOriginAzimuth = (originAzimuth: number | undefined, fallbackAzimuth = 0): number =>
  originAzimuth ?? fallbackAzimuth

export type QueryWithPolylineData = {
  data?: {
    data?: {
      encoded_polyline?: string
    }
  }
}

export const hasUsableRouteData = (query: QueryWithPolylineData): boolean => Boolean(query.data?.data?.encoded_polyline)

export const shouldUseGarageDirections = ({
  navigationMode,
  directionsToNextRouteEnabled,
  routeModuleState,
}: {
  navigationMode: NavigationMode
  directionsToNextRouteEnabled: boolean
  routeModuleState: NavigationDataResponse['route_module_state'] | undefined
}): boolean => {
  if (navigationMode.type === 'MANUAL_GARAGE') {
    return true
  }

  if (navigationMode.type !== 'AUTOMATIC_GARAGE') {
    return false
  }

  if (routeModuleState === 'Driving') {
    return navigationMode.timeRemainingSeconds <= 0
  }

  return !directionsToNextRouteEnabled
}

export const selectDirectionsQuery = <T extends QueryWithPolylineData>({
  shouldNavigateToGarage,
  nextRouteQuery,
  dropoffPointQuery,
  explicitDropoffPointQuery,
}: {
  shouldNavigateToGarage: boolean
  nextRouteQuery: T
  dropoffPointQuery: T
  explicitDropoffPointQuery: T
}): T => {
  if (!shouldNavigateToGarage) {
    return nextRouteQuery
  }

  if (hasUsableRouteData(dropoffPointQuery)) {
    return dropoffPointQuery
  }

  if (hasUsableRouteData(explicitDropoffPointQuery)) {
    return explicitDropoffPointQuery
  }

  return dropoffPointQuery
}

const calculateShouldNavigateToNextRoute = (
  dropoffTime: string | undefined,
  dropoffDriveDuration: number | undefined,
  nextRouteDriveDuration: number | undefined,
  now: Date
) => {
  // If no dropoff time, always navigate to next route
  if (dropoffTime === undefined) {
    return true
  }

  const dropoffDate = new Date(dropoffTime)
  const safeDropoffStart = sub(dropoffDate, { seconds: dropoffDriveDuration ?? DROPOFF.BUFFER_SECONDS })

  // If next route is very close (< 5 min), just drive there
  if ((nextRouteDriveDuration ?? Infinity) < MIN_ROUTE_DURATION_SECONDS) {
    return true
  }

  // Check if we'll finish patrolling BEFORE needing to leave for garage
  const arrivalAtRoute = add(now, { seconds: nextRouteDriveDuration ?? 0 })
  const arrivalWithPatrolTime = add(arrivalAtRoute, { minutes: MIN_PATROLLING_TIME_MINUTES })

  return arrivalWithPatrolTime < safeDropoffStart
}

/**
 * Calculates when automatic shift ending should start (when to begin navigating to garage)
 * This is used for automatic shift ending, not manual garage navigation
 */
const calculateShiftEndingStartDate = (
  dropoffTime: string | undefined,
  dropoffDriveDuration: number | undefined,
  state: string | undefined,
  shouldNavigateToNextRoute: boolean,
  nextRouteDriveDuration: number | undefined,
  now: Date
): Date | undefined => {
  if (dropoffTime === undefined) {
    return undefined
  }

  const dropoffDate = new Date(dropoffTime)
  const safeDropoffStart = sub(dropoffDate, { seconds: dropoffDriveDuration ?? DROPOFF.BUFFER_SECONDS })

  // If no scheduled drive or not navigating to next route, start dropoff now
  if (state === 'NoScheduledDrive' || !shouldNavigateToNextRoute) {
    return now
  }

  // Always use safeDropoffStart for garage alerts (real leave-for-garage time).
  // When navigating to next route (Fetched/Paused), defer until we have both:
  // 1. nextRouteDriveDuration (to know if we should navigate to next route)
  // 2. dropoffDriveDuration (to accurately calculate safeDropoffStart)
  // This avoids showing incorrect countdowns based on stale/missing data.
  if (state === 'Fetched' || state === 'Paused') {
    if (nextRouteDriveDuration === undefined || dropoffDriveDuration === undefined) {
      return undefined
    }
  }

  return safeDropoffStart
}

/**
 * Determines the current navigation mode based on navigation data and timing
 */
const calculateNavigationMode = (
  goToGarage: boolean,
  shiftEndingStartDate: Date | undefined,
  now: Date,
  hasNextRoute: boolean,
  dropoffDriveDuration: number | undefined,
  dropoffTime: string | undefined
): NavigationMode => {
  // Priority 1: Manual garage override (explicit go_to_garage flag)
  if (goToGarage) {
    return { type: 'MANUAL_GARAGE', reason: 'go_to_garage_enabled' }
  }

  // Priority 2: Automatic shift ending (time-based dropoff)
  if (shiftEndingStartDate !== undefined) {
    const secondsToDropoff = differenceInSeconds(shiftEndingStartDate, now)
    const maxAlertSeconds = milliseconds({ minutes: DROPOFF.TIME_TO_DROPOFF_ALERT_MAX_MINUTES }) / 1000

    if (secondsToDropoff <= maxAlertSeconds) {
      return {
        type: 'AUTOMATIC_GARAGE',
        reason: 'shift_ending',
        timeRemainingSeconds: secondsToDropoff,
        dropoffStartDate: shiftEndingStartDate,
        driveDurationSeconds: dropoffDriveDuration ?? 0,
        scheduledEndTime: dropoffTime ?? '',
      }
    }
  }

  // Priority 3: Next route ready (between drives)
  if (hasNextRoute) {
    return { type: 'NEXT_ROUTE', reason: 'between_drives' }
  }

  return { type: 'NONE', reason: 'no_navigation_needed' }
}

/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * Returns distance in meters
 */
const calculateDistanceMeters = (
  coord1: { latitude: number; longitude: number },
  coord2: { latitude: number; longitude: number }
): number => {
  const R = 6371000 // Earth's radius in meters
  const lat1Rad = (coord1.latitude * Math.PI) / 180
  const lat2Rad = (coord2.latitude * Math.PI) / 180
  const deltaLatRad = ((coord2.latitude - coord1.latitude) * Math.PI) / 180
  const deltaLonRad = ((coord2.longitude - coord1.longitude) * Math.PI) / 180

  const a =
    Math.sin(deltaLatRad / 2) * Math.sin(deltaLatRad / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLonRad / 2) * Math.sin(deltaLonRad / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculate the minimum distance (meters) from a point to a polyline
 * defined by an array of [lat, lng] coordinate pairs.
 * Uses perpendicular distance to each line segment for accuracy.
 */
const calculateDistanceToPolyline = (
  point: { latitude: number; longitude: number },
  polylineCoords: Array<[number, number]>
): number => {
  if (polylineCoords.length === 0) return Infinity
  if (polylineCoords.length === 1) {
    return calculateDistanceMeters(point, { latitude: polylineCoords[0][0], longitude: polylineCoords[0][1] })
  }

  let minDistance = Infinity

  for (let i = 0; i < polylineCoords.length - 1; i++) {
    const a = { latitude: polylineCoords[i][0], longitude: polylineCoords[i][1] }
    const b = { latitude: polylineCoords[i + 1][0], longitude: polylineCoords[i + 1][1] }

    const dist = pointToSegmentDistance(point, a, b)
    if (dist < minDistance) minDistance = dist
  }

  return minDistance
}

/**
 * Perpendicular distance from point P to line segment AB (in meters).
 * Projects P onto AB; if projection falls outside, uses nearest endpoint.
 */
const pointToSegmentDistance = (
  p: { latitude: number; longitude: number },
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number }
): number => {
  const dx = b.longitude - a.longitude
  const dy = b.latitude - a.latitude
  const lenSq = dx * dx + dy * dy

  if (lenSq === 0) return calculateDistanceMeters(p, a)

  let t = ((p.longitude - a.longitude) * dx + (p.latitude - a.latitude) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))

  const proj = {
    latitude: a.latitude + t * dy,
    longitude: a.longitude + t * dx,
  }

  return calculateDistanceMeters(p, proj)
}

export const useDirectionsData = ({
  navigationQuery,
  client,
}: {
  navigationQuery: UseQueryResult<NavigationDataResponse>
  client: Client | undefined
}) => {
  const now = useCurrentTime(1000)
  const navigationData = navigationQuery.data
  const state = navigationData?.route_module_state
  const { originCoord, originAzimuth } = extractVehicleData(navigationData)

  const dropoffTime = navigationData?.scheduled_shift?.dropoff_time
  const dropoffPoint = navigationData?.scheduled_shift?.dropoff_point
  const nextSegment = navigationData?.future_street_segments?.[0]

  // Extract flags
  const isManualGarage = navigationData?.go_to_garage === true
  const hasNextRoute = navigationData?.drive_data?.start_after !== undefined

  // Determine if directions should be enabled for next route
  const directionsToNextRouteEnabled = shouldEnableNextRouteDirections(navigationData, state, originCoord)

  // Query: Directions to next route segment
  const nextRouteQueryKey = buildNextRouteQueryKey(nextSegment?.street_segment_id, dropoffPoint)

  const nextRouteQuery = useQuery({
    queryKey: nextRouteQueryKey,
    gcTime: 0,
    staleTime: Infinity,
    queryFn: () => {
      // Use non-null assertions to match original behavior when enabled is true
      const segment = navigationData?.future_street_segments?.[0]
      if (!segment) throw new Error('No future segment found')

      const sourceNode = determineSourceNode(segment)
      if (!sourceNode) throw new Error('No source node found')

      const destinationNode = determineDestinationNode(segment)
      if (!destinationNode) throw new Error('No destination node found')

      // Calculate the direction we'll drive the segment (from source to destination)
      const segmentDirection = calculateAzimuth(sourceNode, destinationNode)
      const effectiveOriginAzimuth = resolveOriginAzimuth(originAzimuth, segmentDirection)

      // Use corrected azimuth to ensure arrival direction doesn't require >180° turn
      // from vehicle's current heading, helping avoid U-turns
      const arrivalAzimuth = calculateCorrectedAzimuth(effectiveOriginAzimuth, segmentDirection)

      return directions({
        client,
        body: {
          origin_coord: originCoord!,
          origin_azimuth: effectiveOriginAzimuth,
          destination_coord: sourceNode,
          arrival_azimuth: arrivalAzimuth,
        },
      })
    },
    enabled: directionsToNextRouteEnabled,
    ...STATIC_QUERY_OPTIONS,
  })

  const nextRouteDriveDuration = nextRouteQuery.data?.data?.duration_s

  // Query: Directions to dropoff point
  // Note: Must use navigationData directly to match original query key structure
  const dropoffPointQueryKey = buildDropoffPointQueryKey(navigationData)

  const dropoffPointQuery = useQuery({
    queryKey: dropoffPointQueryKey,
    gcTime: 0,
    staleTime: milliseconds({ minutes: DROPOFF.QUERY_STALE_MINUTES }),
    enabled:
      originCoord !== undefined &&
      dropoffPoint !== undefined &&
      dropoffTime !== undefined &&
      isAfter(add(now, { minutes: DROPOFF.QUERY_START_MINUTES_BEFORE }), new Date(dropoffTime)),
    queryFn: () =>
      directions({
        client,
        body: {
          origin_coord: originCoord!,
          origin_azimuth: resolveOriginAzimuth(originAzimuth),
          destination_coord: dropoffPoint!,
          arrival_azimuth: 0,
        },
      }),
  })

  const dropoffDriveDuration = dropoffPointQuery.data?.data?.duration_s

  // Query: Directions to explicit dropoff point (hardcoded coordinates)
  const explicitDropoffPointQueryKey = buildExplicitDropoffPointQueryKey(nextSegment?.street_segment_id, dropoffPoint)

  const explicitDropoffPointQuery = useQuery({
    queryKey: explicitDropoffPointQueryKey,
    gcTime: 0,
    staleTime: Infinity,
    queryFn: () => {
      return directions({
        client,
        body: {
          origin_coord: originCoord!,
          origin_azimuth: resolveOriginAzimuth(originAzimuth),
          destination_coord: EXPLICIT_DROPOFF_POINT,
          arrival_azimuth: 0,
        },
      })
    },
    enabled: originCoord !== undefined && (isManualGarage || state === 'NoScheduledDrive'),
    ...STATIC_QUERY_OPTIONS,
  })

  // Calculate whether we should navigate to next route (before shift ends)
  const shouldNavigateToNextRoute = calculateShouldNavigateToNextRoute(
    dropoffTime,
    dropoffDriveDuration,
    nextRouteDriveDuration,
    now
  )

  // Stabilize dropoff drive duration to prevent countdown jumps
  const stableDropoffDriveDuration = useRef<number | undefined>(undefined)
  if (dropoffDriveDuration !== undefined && stableDropoffDriveDuration.current === undefined) {
    stableDropoffDriveDuration.current = dropoffDriveDuration
  }

  // Stabilize shift ending update to prevent jumps (e.g. when next route duration loads)
  const stableShiftEndingStartDate = useRef<Date | undefined>(undefined)

  // Calculate raw value
  const rawShiftEndingStartDate = calculateShiftEndingStartDate(
    dropoffTime,
    stableDropoffDriveDuration.current ?? dropoffDriveDuration,
    state,
    shouldNavigateToNextRoute,
    nextRouteDriveDuration,
    now
  )

  // Update stable value logic
  if (
    // Initialize
    stableShiftEndingStartDate.current === undefined ||
    rawShiftEndingStartDate === undefined ||
    // Always update if we switched from defined to undefined or vice versa (handled by !== undefined check implied above)
    // If calculating:
    (stableShiftEndingStartDate.current !== undefined && rawShiftEndingStartDate !== undefined)
  ) {
    if (stableShiftEndingStartDate.current === undefined || rawShiftEndingStartDate === undefined) {
      stableShiftEndingStartDate.current = rawShiftEndingStartDate
    } else {
      const diffSeconds = differenceInSeconds(rawShiftEndingStartDate, stableShiftEndingStartDate.current)

      // Update if:
      // 1. Time moved EARLIER (diff < -60) -> User has LESS time, show urgency immediately
      // 2. Time moved LATER by a LOT (diff > 10 min) -> Major route change, allow jump
      // 3. Otherwise (Time moved later by small amount, e.g. 3 min) -> Keep conservative estimate to avoid visual jump
      if (diffSeconds < -60 || diffSeconds > 600) {
        stableShiftEndingStartDate.current = rawShiftEndingStartDate
      }
    }
  }

  const shiftEndingStartDate = stableShiftEndingStartDate.current

  // Determine the current navigation mode
  const navigationMode = calculateNavigationMode(
    isManualGarage,
    shiftEndingStartDate,
    now,
    hasNextRoute,
    stableDropoffDriveDuration.current ?? dropoffDriveDuration,
    dropoffTime
  )

  const shouldNavigateToGarage = shouldUseGarageDirections({
    navigationMode,
    directionsToNextRouteEnabled,
    routeModuleState: state,
  })

  const directionsQuery = selectDirectionsQuery({
    shouldNavigateToGarage,
    nextRouteQuery,
    dropoffPointQuery,
    explicitDropoffPointQuery,
  })

  // Extract time remaining for backward compatibility
  const secondsToDropoff =
    navigationMode.type === 'AUTOMATIC_GARAGE'
      ? navigationMode.timeRemainingSeconds
      : navigationMode.type === 'MANUAL_GARAGE'
        ? 0
        : Infinity

  // Backward compatibility: shouldStartDropoff (deprecated, use shouldNavigateToGarage)
  const shouldStartDropoff = shouldNavigateToGarage

  // Backward compatibility: dropoffStartDate (deprecated, use navigationMode.dropoffStartDate)
  const dropoffStartDate =
    navigationMode.type === 'AUTOMATIC_GARAGE' ? navigationMode.dropoffStartDate : shiftEndingStartDate

  // Auto-refetch when vehicle deviates > 200m from the drawn navigation polyline
  const lastRefetchTimeRef = useRef<number>(0)

  useEffect(() => {
    if (!originCoord || !directionsQuery.data?.data?.encoded_polyline) {
      return
    }

    const polylineCoords = decodePolyline(directionsQuery.data.data.encoded_polyline)
    const deviation = calculateDistanceToPolyline(originCoord, polylineCoords)

    const nowMs = Date.now()
    if (deviation > AUTO_REFETCH_DISTANCE_METERS && nowMs - lastRefetchTimeRef.current > REFETCH_COOLDOWN_MS) {
      lastRefetchTimeRef.current = nowMs
      directionsQuery.refetch()
    }
  }, [originCoord, directionsQuery])

  return {
    // Navigation mode (primary source of truth)
    navigationMode,

    // Computed values for convenience
    shouldNavigateToGarage,
    isManualGarage,
    isShiftEnding: navigationMode.type === 'AUTOMATIC_GARAGE',
    secondsToDropoff,
    shiftEndingStartDate: navigationMode.type === 'AUTOMATIC_GARAGE' ? navigationMode.dropoffStartDate : undefined,
    /** Drive duration to garage (seconds). Used for drive-aware NoScheduledDrive GoToGarage threshold. */
    dropoffDriveDurationSeconds: dropoffDriveDuration,

    // Directions
    directionsEnabled: directionsToNextRouteEnabled || shouldNavigateToGarage,
    directionsQuery,

    // Queries (for refetching)
    explicitDropoffPointQuery,
    nextRouteQueryKey,
    dropoffPointQueryKey,
    explicitDropoffPointQueryKey,
    refreshDirections: () => directionsQuery.refetch(),
    explicitDirectionsToGarage: () => explicitDropoffPointQuery.refetch(),

    // Legacy support (deprecated, use navigationMode instead)
    shouldStartDropoff, // Keep for backward compatibility
    dropoffStartDate, // Keep for backward compatibility
  }
}
